import { apiMessage, metric, postJson } from '../api';
import { L } from '../state';
import { listen, speak } from '../speech';
import { escapeHtml, modalBody, need, show } from '../ui/modal';

type Direction = 'passenger_to_driver' | 'driver_to_passenger';

async function translate(direction: Direction, text: string): Promise<void> {
  const lang = L();
  const out = need('#interpOut');
  if (!text.trim()) return;

  out.innerHTML = '<div class="thinking">…</div>';
  try {
    const { ok, data } = await postJson<{ translation?: string; error?: string; message?: string }>(
      '/api/interpreter.php',
      { direction, text: text.trim(), passengerLanguageName: lang.name },
      45_000,
    );

    if (!ok || !data.translation) {
      void metric('interpreter_error');
      out.textContent = apiMessage(data, lang.err, lang.form.auth);
      return;
    }

    void metric('interpreter_ok');
    out.innerHTML = '';

    const heard = document.createElement('div');
    heard.className = 'interp-heard';
    heard.innerHTML = `<strong>${escapeHtml(lang.heard)}:</strong> ${escapeHtml(text.trim())}`;

    const result = document.createElement('div');
    result.className = 'interp-result';
    result.innerHTML = `<strong>${escapeHtml(lang.translation)}:</strong><br>${escapeHtml(data.translation)}`;

    out.append(heard, result);

    const voiceLocale = direction === 'passenger_to_driver' ? 'es-ES' : lang.locale;
    const replay = document.createElement('button');
    replay.className = 'speakbtn';
    replay.type = 'button';
    replay.textContent = '🔊';
    replay.onclick = () => speak(data.translation!, voiceLocale, true);
    result.append(' ', replay);

    speak(data.translation, voiceLocale);
  } catch {
    void metric('interpreter_error');
    out.textContent = lang.err;
  }
}

function startListening(direction: Direction): void {
  void metric('interpreter_voice_start');
  const lang = L();
  const status = need('#voiceStatus');

  status.textContent = lang.listening;
  status.classList.add('listening');

  const started = listen({
    locale: direction === 'passenger_to_driver' ? lang.locale : 'es-ES',
    onResult: (transcript) => {
      need<HTMLTextAreaElement>('#manualText').value = transcript;
      void translate(direction, transcript);
    },
    onError: (kind) => {
      status.textContent = kind === 'denied' ? lang.micHelp : lang.voiceUnavailable;
      status.classList.remove('listening');
    },
    onEnd: () => {
      status.classList.remove('listening');
      if (status.textContent === lang.listening) status.textContent = lang.listen;
    },
  });

  if (!started) {
    status.textContent = lang.voiceUnavailable;
    status.classList.remove('listening');
  }
}

export function interpreter(): void {
  void metric('interpreter_open');
  const lang = L();

  show(lang.interpreter, `
    <div class="interp-tabs">
      <button class="interp-action passenger-action" data-dir="passenger_to_driver" type="button">
        <span>🎙️</span><b>${escapeHtml(lang.passengerSpeak)}</b><small>${escapeHtml(lang.toSpanish)}</small>
      </button>
      <button class="interp-action driver-action" data-dir="driver_to_passenger" type="button">
        <span>🎙️</span><b>${escapeHtml(lang.driverSpeak)}</b><small>${escapeHtml(lang.toPassenger)}</small>
      </button>
    </div>
    <p id="voiceStatus" class="voice-status">${escapeHtml(lang.listen)}</p>
    <div class="manualbox">
      <textarea id="manualText" rows="3" maxlength="3000" placeholder="${escapeHtml(lang.manual)}"></textarea>
      <div class="manual-actions">
        <button id="manualPassenger" class="mini" type="button">${escapeHtml(lang.toSpanish)}</button>
        <button id="manualDriver" class="mini" type="button">${escapeHtml(lang.toPassenger)}</button>
      </div>
    </div>
    <div id="interpOut" class="interp-out"></div>
    <p class="fineprint">${escapeHtml(lang.micHelp)}</p>`);

  for (const button of modalBody().querySelectorAll<HTMLButtonElement>('[data-dir]')) {
    button.onclick = () => startListening(button.dataset.dir as Direction);
  }
  need('#manualPassenger').onclick = () =>
    void translate('passenger_to_driver', need<HTMLTextAreaElement>('#manualText').value);
  need('#manualDriver').onclick = () =>
    void translate('driver_to_passenger', need<HTMLTextAreaElement>('#manualText').value);
}
